import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useExtendedState from '../../hooks/useExtendedState';
import { productPropertiesType, productType as productObjType } from '../../types';
import Privileges from '../../utils/Privileges';
import { Privileged } from '../../hocs/withPrivileges';
import PrivilegedColorField from '../fields/privileged/PrivilegedColorField';
import PrivilegedIconField from '../fields/privileged/PrivilegedIconField';
import PrivilegedSelectField from '../fields/privileged/PrivilegedSelectField';
import PrivilegedTextField from '../fields/privileged/PrivilegedTextField';
import ReadonlyField from '../fields/ReadonlyField';
import './_styles_/ProductDetailTab.css';
import ApplyChangesButton from '../buttons/ApplyChangesButton';
import useExtendedMutation from '../../hooks/useExtendedMutation';
import {
    ALL_PRODUCTS_QUERY_NAME,
    TOGGLE_PRODUCT_ACTIVATION,
    UPDATE_PRODUCT,
} from '../../graphql/products';
import useMultiValidator from '../../hooks/useMultiValidator';
import {
    isinCodeValidator,
    PRODUCT_ISIN_CODE_LENGTH,
    PRODUCT_NAME_MAX_LENGTH,
    productNameValidator,
} from '../../validators/products';
import ActivationButton from '../buttons/ActivationButton';
import { resolveUnit } from '../Unit';
import {
    ENERGY,
    getProductType,
    SECURITY,
} from '../../utils/ProductTypes';
import ExpandedListTab from '../ExpandedListTab';
import InfoPanel from '../InfoPanel';
import { transformProductDeactivationBlockers, transformProductModificationBlockers } from '../blockers/ProductBlockers';
import { canFieldBeModified } from '../../utils/fields';
import useIsMounted from '../../hooks/useIsMounted';
import { compositeValidator, wafValidator } from '../../hooks/validationSupport';

ProductDetailTab.propTypes = {
    product: productObjType.isRequired,
    productProps: productPropertiesType.isRequired,
};

function ProductDetailTab(props) { // NOSONAR javascript:S3776
    const {
        product,
        productProps,
    } = props;
    const { t } = useTranslation();
    const {
        productId,
        productType: serverProductType,
    } = product;
    const isMounted = useIsMounted();
    const productType = getProductType(serverProductType);
    const activeState = useExtendedState(product.active);
    const nameState = useExtendedState(product.name);
    const isinCodeState = useExtendedState(product.isinCode);
    const iconSymbolState = useExtendedState(product.iconSymbol);
    const iconColorState = useExtendedState(product.iconColor);
    const deliveryTypeState = useExtendedState(product.deliveryType);
    const deliveryPeriodState = useExtendedState(product.deliveryPeriod);
    const quantityUnitState = useExtendedState(product.quantityUnit);
    const {
        availableDeliveryTypes,
        availableDeliveryPeriods,
        availableQuantityUnits,
        availableProductTypes,
    } = productProps;
    const { validators, isValid } = useMultiValidator(true, {
        name: compositeValidator(productNameValidator(t), wafValidator(t)),
        isinCode: compositeValidator(isinCodeValidator(t), wafValidator(t)),
    });
    const isChangedPromise = nameState.isChanged || deliveryTypeState.isChanged || deliveryPeriodState.isChanged || quantityUnitState.isChanged || iconSymbolState.isChanged || iconColorState.isChanged || isinCodeState.isChanged ? Promise.resolve() : Promise.reject();
    const [isDisabled, setIsDisabled] = useState(true);
    Promise.all([isChangedPromise, isValid()])
        .then(() => setIsDisabled(false))
        .catch(() => setIsDisabled(true));

    const isActivationButtonDisabled = activeState.value && product.deactivationBlockers?.length > 0;
    let toggleProductActivationConfirmMessageKey = 'products.confirm-activate-product';
    let toggleProductActivationFailureMessageKey = 'products.notification.product-activate-failure';
    if (activeState.value) {
        toggleProductActivationConfirmMessageKey = 'products.confirm-deactivate-product';
        toggleProductActivationFailureMessageKey = 'products.notification.product-deactivate-failure';
    }
    const statesToResetOnSuccess = [nameState, isinCodeState, iconSymbolState, iconColorState, deliveryTypeState, deliveryPeriodState, quantityUnitState];
    const toggleProductActivationMutation = useExtendedMutation(TOGGLE_PRODUCT_ACTIVATION, {
        variables: { id: product.id },
        successCallback: () => {
            if (isMounted()) {
                activeState.changeInitialState(!activeState.value);
                activeState.reset();
            }
        },
        successMessageCreator: ({ toggleProductActivation: { active: updatedActive, name: updatedName, productId: updatedProductId } }) => {
            let toggleProductActivationSuccessMessageKey = 'products.notification.product-deactivate-success';
            if (updatedActive) {
                toggleProductActivationSuccessMessageKey = 'products.notification.product-activate-success';
            }
            return t(toggleProductActivationSuccessMessageKey, { name: updatedName, productId: updatedProductId });
        },
        failureMessageOptions: { key: toggleProductActivationFailureMessageKey, params: { name: product.name, productId: product.productId } },
        refetchQueries: [ALL_PRODUCTS_QUERY_NAME],
    });
    const updateProductMutation = useExtendedMutation(UPDATE_PRODUCT, {
        variables: {
            id: product.id,
            productId,
            name: nameState.value,
            isinCode: isinCodeState.value,
            iconSymbol: iconSymbolState.value,
            iconColor: iconColorState.value,
            deliveryType: deliveryTypeState.value,
            deliveryPeriod: deliveryPeriodState.value,
            quantityUnit: quantityUnitState.value,
        },
        statesToResetOnSuccess,
        successMessageOptions: { key: 'products.notification.product-update-success', dataProperty: 'updateProduct' },
        failureMessageOptions: { key: 'products.notification.product-update-failure', params: { name: product.name, productId: product.productId } },
        refetchQueries: [ALL_PRODUCTS_QUERY_NAME],
    });
    const sufficientPrivileges = [Privileges.PRODUCT_EDIT];
    return (
        <ExpandedListTab
            className="product-detail-tab"
            title={t('products.product-detail')}
        >
            <div className="product-detail-tab--readonly-fields">
                <ReadonlyField
                    label={t('products.product-id')}
                    value={productId}
                    className="product-detail-tab--fields--product-id"
                />
                {availableProductTypes.length > 1 && (
                    <ReadonlyField
                        label={t('products.product-type')}
                        value={productType.translate(t)}
                        className="product-detail-tab--fields--product-type"
                    />
                )}
                <ReadonlyField
                    label={t('products.active')}
                    value={activeState.value ? t('misc.yes') : t('misc.no')}
                    className="product-detail-tab--fields--active"
                />
            </div>
            <div className="product-detail-tab--fields">
                <PrivilegedTextField
                    sufficientPrivileges={canFieldBeModified('name', product.modificationBlockers) ? sufficientPrivileges : []}
                    label={t('products.name')}
                    state={nameState}
                    validator={validators.name}
                    className="product-detail-tab--fields--name wide"
                    placeholder={t('products.placeholder.name')}
                    maxLength={PRODUCT_NAME_MAX_LENGTH}
                    isRequired
                />
                <PrivilegedIconField
                    sufficientPrivileges={canFieldBeModified('iconSymbol', product.modificationBlockers) ? sufficientPrivileges : []}
                    label={t('products.icon-symbol')}
                    state={iconSymbolState}
                    inputId="product-detail-tab--fields--icon-symbol"
                    className="product-detail-tab--fields--icon-symbol"
                />
                <PrivilegedColorField
                    sufficientPrivileges={canFieldBeModified('iconColor', product.modificationBlockers) ? sufficientPrivileges : []}
                    label={t('products.icon-color')}
                    state={iconColorState}
                    inputId="product-detail-tab--fields--icon-color"
                    className="product-detail-tab--fields--icon-color"
                />
                {productType.is(ENERGY) && (
                    <>
                        <PrivilegedSelectField
                            sufficientPrivileges={canFieldBeModified('deliveryType', product.modificationBlockers) ? sufficientPrivileges : []}
                            label={t('products.delivery-type')}
                            state={deliveryTypeState}
                            className="product-detail-tab--fields--delivery-type"
                            inputId="product-detail-tab--fields--delivery-type"
                            options={availableDeliveryTypes.map((type) => <option value={type} key={type}>{type}</option>)}
                            isRequired
                        />
                        <PrivilegedSelectField
                            sufficientPrivileges={canFieldBeModified('deliveryPeriod', product.modificationBlockers) ? sufficientPrivileges : []}
                            label={t('products.delivery-period')}
                            state={deliveryPeriodState}
                            className="product-detail-tab--fields--delivery-period"
                            inputId="product-detail-tab--fields--delivery-period"
                            options={availableDeliveryPeriods.map((period) => <option value={period} key={period}>{period}</option>)}
                            isRequired
                        />
                        <PrivilegedSelectField
                            sufficientPrivileges={canFieldBeModified('quantityUnit', product.modificationBlockers) ? sufficientPrivileges : []}
                            label={t('products.quantity-unit')}
                            state={quantityUnitState}
                            className="product-detail-tab--fields--quantity-unit"
                            inputId="product-detail-tab--fields--quantity-unit"
                            options={availableQuantityUnits.map((unit) => <option value={unit} key={unit}>{resolveUnit(unit, t)}</option>)}
                            isRequired
                        />
                    </>
                )}
                {productType.is(SECURITY) && (
                    <PrivilegedTextField
                        sufficientPrivileges={canFieldBeModified('isinCode', product.modificationBlockers) ? sufficientPrivileges : []}
                        label={t('products.isin-code')}
                        state={isinCodeState}
                        validator={validators.isinCode}
                        className="product-detail-tab--fields--isin-code wide"
                        placeholder={t('products.placeholder.isin-code')}
                        maxLength={PRODUCT_ISIN_CODE_LENGTH}
                        isRequired
                    />
                )}
            </div>
            <Privileged sufficientPrivileges={[Privileges.PRODUCT_EDIT]}>
                <div className="rich-list-row-expanded--buttons">
                    <div className="rich-list-row-expanded--buttons--left">
                        <ApplyChangesButton
                            disabled={isDisabled}
                            extendedMutation={updateProductMutation}
                            confirmationMessage={t('products.confirm-update-product', { name: product.name, productId: product.productId })}
                        />
                    </div>
                    <div className="rich-list-row-expanded--buttons--middle">
                        <ActivationButton
                            isActive={activeState.value}
                            disabled={isActivationButtonDisabled}
                            extendedMutation={toggleProductActivationMutation}
                            confirmationMessage={t(toggleProductActivationConfirmMessageKey, { name: product.name, productId: product.productId })}
                        />
                    </div>
                    <div className="rich-list-row-expanded--buttons--right" />
                </div>
                {product?.modificationBlockers?.blockerConstants.map((modificationBlocker) => (
                    <InfoPanel
                        key={modificationBlocker}
                        message={transformProductModificationBlockers(t, modificationBlocker)}
                    />
                ))}
                {product?.deactivationBlockers?.map((deactivationBlocker) => (
                    <InfoPanel
                        key={deactivationBlocker}
                        message={transformProductDeactivationBlockers(t, deactivationBlocker)}
                    />
                ))}
            </Privileged>
        </ExpandedListTab>
    );
}

export default ProductDetailTab;
